import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import supabase from "../../../../utils/supabase";
import { useStatusDialog } from "../../../common/StatusDialogProvider";
import ScientificNameSelector from "./parts/ScientificNameSelector";
import { chunk, selectInBatched } from "../../../../utils/supabase-batch";

const COLUMN_ALIASES = {
  status: ["current status", "status", "current_rank", "current rank"],
  currentParent: ["current parent", "current_parent", "parent", "current parent name"],
  originalParent: ["original parent", "original_parent", "orig parent"],
  scientificName: ["scientific name", "name", "scientific_name", "taxon"],
  authority: ["authority", "author", "authorship"],
  year: ["year", "authority year", "authorship year"],
};

const TAXAPAD_ALIASES = {
  taxonId: ["taxon_id", "taxon id"],
  parentId: ["parent_id", "parent id"],
  rank: ["rank"],
  nameRank: ["name_rank", "name rank"],
  taxonomicStatus: ["taxonomic_status", "taxonomic status", "status"],
  taxonomicRelation: ["taxonomic_relation", "taxonomic relation", "relation"],
  scientificName: ["scientific_name", "scientific name"],
  authorship: ["authorship", "author"],
  year: ["year"],
  authorInParens: ["author_in_parens", "author in parens", "author in parentheses"],
  originalParent: ["original_parent", "original parent"],
  notes: ["notes", "note"],
  indent: ["indent"],
  lineNo: ["line_no", "line no"],
};

const FORMAT_PARENT = "parent_name";
const FORMAT_TAXAPAD = "taxapad2016";

const INVALID_RANK_KEYWORDS = new Set([
  "invalid",
  "synonym",
  "junior synonym",
  "senior synonym",
  "objective synonym",
  "subjective synonym",
  "homonym",
]);

const normalizeHeader = (val) =>
  (val ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeText = (val) => (val ?? "").toString().trim();

const normalizeKey = (val) =>
  normalizeText(val).normalize("NFKC").toLowerCase().replace(/\s+/g, " ");

const normalizeRank = (val) => normalizeKey(val);

const parseYear = (val) => {
  const raw = normalizeText(val);
  if (!raw) return null;
  const num = Number.parseInt(raw, 10);
  return Number.isFinite(num) ? num : null;
};

const parseBool = (val) => {
  if (typeof val === "boolean") return val;
  const raw = normalizeText(val).toLowerCase();
  if (!raw) return false;
  return raw === "true" || raw === "1" || raw === "yes" || raw === "y";
};

const parseInteger = (val) => {
  const raw = normalizeText(val);
  if (!raw) return null;
  const num = Number.parseInt(raw, 10);
  return Number.isFinite(num) ? num : null;
};

const normalizeParentId = (val) => {
  const raw = normalizeText(val);
  if (!raw) return "";
  const lowered = raw.toLowerCase();
  if (lowered === "0" || lowered === "null" || lowered === "none") return "";
  return raw;
};

const buildAuthorityFromParts = (authorship, year, authorInParens) => {
  const author = normalizeText(authorship);
  const yearPart = normalizeText(year);
  const parts = [author, yearPart].filter(Boolean);
  if (!parts.length) return null;
  const base = parts.join(", ");
  if (!authorInParens) return base;
  if (base.startsWith("(") && base.endsWith(")")) return base;
  return `(${base})`;
};

const generateId = (prefix = "sci_name") => {
  const guid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  return `${prefix}_${guid}`;
};

const createSelectionModel = (ids = [], type = "include") => ({
  type: type === "exclude" ? "exclude" : "include",
  ids: new Set((ids || []).map((id) => String(id))),
});

const selectionModelIds = (model) => Array.from(model?.ids || []).map((id) => String(id));

const normalizeSelectionModel = (model) => {
  if (!model) return createSelectionModel();
  if (model?.type && model?.ids instanceof Set) return model;
  if (Array.isArray(model)) return createSelectionModel(model);
  if (Array.isArray(model?.ids)) return createSelectionModel(model.ids, model.type);
  if (model?.ids instanceof Set) return createSelectionModel(Array.from(model.ids), model.type);
  return createSelectionModel();
};

const appendRemark = (current, tag) => {
  const base = normalizeText(current);
  if (!tag) return base || null;
  if (!base) return tag;
  if (base.includes(tag)) return base;
  return `${base}; ${tag}`;
};

const buildRemarkTag = (fileName) => {
  const date = new Date().toISOString().slice(0, 10);
  const namePart = fileName ? ` (${fileName})` : "";
  return `Imported from Excel${namePart} on ${date}`;
};

const isInvalidStatus = (status) => {
  const norm = normalizeRank(status);
  if (!norm) return false;
  if (INVALID_RANK_KEYWORDS.has(norm)) return true;
  if (norm.includes("synonym") || norm.includes("homonym")) return true;
  return false;
};

const isSynonymLabel = (value) => {
  const norm = normalizeRank(value);
  if (!norm) return false;
  if (norm.includes("synonym")) return true;
  return norm === "syn" || norm === "syn.";
};

const LOWER_RANK_EXCLUSIONS = new Set([
  "variety",
  "var.",
  "forma",
  "form",
  "f.",
  "subvariety",
  "subform",
  "aberration",
  "ab.",
  "infraspecies",
  "infraspecific",
]);

const shouldCapitalizeRank = (rank) => {
  const norm = normalizeRank(rank);
  if (!norm) return false;
  if (LOWER_RANK_EXCLUSIONS.has(norm)) return false;
  return true;
};

const formatScientificNameByRank = (name, rank) => {
  const raw = normalizeText(name);
  if (!raw) return raw;
  if (!shouldCapitalizeRank(rank)) return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const parseExcelFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return { format: FORMAT_PARENT, rows: [] };
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return { format: FORMAT_PARENT, rows: [] };
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  if (!rows.length) return { format: FORMAT_PARENT, rows: [] };

  const [headerRow, ...dataRows] = rows;
  const headerMap = new Map();
  headerRow.forEach((cell, idx) => {
    const key = normalizeHeader(cell);
    if (key) headerMap.set(key, idx);
  });

  const findIndex = (aliases) => {
    for (const alias of aliases) {
      const idx = headerMap.get(normalizeHeader(alias));
      if (idx != null) return idx;
    }
    return -1;
  };

  const hasTaxonId = headerMap.has("taxon_id");
  const hasParentId = headerMap.has("parent_id");
  const hasScientificName = headerMap.has("scientific_name");
  const format = hasTaxonId && hasParentId && hasScientificName ? FORMAT_TAXAPAD : FORMAT_PARENT;

  if (format === FORMAT_TAXAPAD) {
    const taxonIdIdx = findIndex(TAXAPAD_ALIASES.taxonId);
    const parentIdIdx = findIndex(TAXAPAD_ALIASES.parentId);
    const rankIdx = findIndex(TAXAPAD_ALIASES.rank);
    const nameRankIdx = findIndex(TAXAPAD_ALIASES.nameRank);
    const taxonomicStatusIdx = findIndex(TAXAPAD_ALIASES.taxonomicStatus);
    const taxonomicRelationIdx = findIndex(TAXAPAD_ALIASES.taxonomicRelation);
    const scientificNameIdx = findIndex(TAXAPAD_ALIASES.scientificName);
    const authorshipIdx = findIndex(TAXAPAD_ALIASES.authorship);
    const yearIdx = findIndex(TAXAPAD_ALIASES.year);
    const authorInParensIdx = findIndex(TAXAPAD_ALIASES.authorInParens);
    const originalParentIdx = findIndex(TAXAPAD_ALIASES.originalParent);
    const notesIdx = findIndex(TAXAPAD_ALIASES.notes);
    const indentIdx = findIndex(TAXAPAD_ALIASES.indent);
    const lineNoIdx = findIndex(TAXAPAD_ALIASES.lineNo);

    const missing = [];
    if (taxonIdIdx < 0) missing.push("TAXON_ID");
    if (parentIdIdx < 0) missing.push("PARENT_ID");
    if (rankIdx < 0) missing.push("RANK");
    if (scientificNameIdx < 0) missing.push("SCIENTIFIC_NAME");
    if (missing.length) {
      throw new Error(`Missing required column(s): ${missing.join(", ")}`);
    }

    const cleaned = [];
    dataRows.forEach((row, idx) => {
      if (!Array.isArray(row)) return;
      const rowIndex = idx + 2;
      const getVal = (i) => normalizeText(i >= 0 ? row[i] : "");
      const taxonId = getVal(taxonIdIdx);
      const parentId = getVal(parentIdIdx);
      const rank = getVal(rankIdx);
      const nameRank = getVal(nameRankIdx);
      const taxonomicStatus = getVal(taxonomicStatusIdx);
      const taxonomicRelation = getVal(taxonomicRelationIdx);
      const scientificName = getVal(scientificNameIdx);
      const authorship = getVal(authorshipIdx);
      const year = getVal(yearIdx);
      const authorInParens = getVal(authorInParensIdx);
      const originalParent = getVal(originalParentIdx);
      const notes = getVal(notesIdx);
      const indent = getVal(indentIdx);
      const lineNo = getVal(lineNoIdx);

      const isEmpty = ![taxonId, parentId, rank, scientificName, authorship, year, notes].some(Boolean);
      if (isEmpty) return;

      cleaned.push({
        rowIndex,
        taxonId,
        parentId,
        rank,
        nameRank,
        taxonomicStatus,
        taxonomicRelation,
        scientificName,
        authorship,
        year,
        authorInParens,
        originalParent,
        notes,
        indent,
        lineNo,
      });
    });
    return { format, rows: cleaned };
  }

  const statusIdx = findIndex(COLUMN_ALIASES.status);
  const currentParentIdx = findIndex(COLUMN_ALIASES.currentParent);
  const originalParentIdx = findIndex(COLUMN_ALIASES.originalParent);
  const nameIdx = findIndex(COLUMN_ALIASES.scientificName);
  const authorityIdx = findIndex(COLUMN_ALIASES.authority);
  const yearIdx = findIndex(COLUMN_ALIASES.year);

  const missing = [];
  if (statusIdx < 0) missing.push("CURRENT STATUS");
  if (currentParentIdx < 0) missing.push("CURRENT PARENT");
  if (nameIdx < 0) missing.push("SCIENTIFIC NAME");
  if (missing.length) {
    throw new Error(`Missing required column(s): ${missing.join(", ")}`);
  }

  const cleaned = [];
  dataRows.forEach((row, idx) => {
    if (!Array.isArray(row)) return;
    const rowIndex = idx + 2;
    const getVal = (i) => normalizeText(i >= 0 ? row[i] : "");
    const status = getVal(statusIdx);
    const name = getVal(nameIdx);
    const currentParentName = getVal(currentParentIdx);
    const originalParentName = getVal(originalParentIdx);
    const authority = getVal(authorityIdx);
    const year = getVal(yearIdx);
    const isEmpty = ![status, name, currentParentName, originalParentName, authority, year].some(Boolean);
    if (isEmpty) return;
    cleaned.push({
      rowIndex,
      status,
      name,
      currentParentName,
      originalParentName,
      authority,
      year,
    });
  });
  return { format, rows: cleaned };
};

const omitUndefined = (payload) => {
  const next = {};
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined) next[key] = value;
  });
  return next;
};

const detectOptionalColumns = async (columns) => {
  const availability = {};
  const names = Array.isArray(columns) ? columns : [];
  if (!names.length) return { available: availability, warning: "" };
  try {
    const { data, error } = await supabase.from("scientific_names").select("*").limit(1);
    if (error) throw error;
    if (data?.length) {
      const keys = new Set(Object.keys(data[0] || {}));
      names.forEach((col) => {
        availability[col] = keys.has(col);
      });
    } else {
      for (const col of names) {
        // Fallback check when table is empty.
        const { error: colError } = await supabase.from("scientific_names").select(col).limit(1);
        if (colError) {
          const msg = (colError.message || "").toLowerCase();
          if (msg.includes("does not exist")) {
            availability[col] = false;
          } else {
            throw colError;
          }
        } else {
          availability[col] = true;
        }
      }
    }
  } catch (err) {
    names.forEach((col) => {
      availability[col] = false;
    });
    return {
      available: availability,
      warning: `Unable to verify optional columns (${names.join(", ")}); skipping them.`,
    };
  }

  const missing = names.filter((col) => !availability[col]);
  const warning = missing.length
    ? `Optional columns not found in scientific_names: ${missing.join(", ")}. Values will be skipped.`
    : "";
  return { available: availability, warning };
};

const buildInsertPayload = ({ row, currentParentId, originalParentId, validNameId, remarkTag }) => ({
  id: row.newId,
  name_spell_valid: row.name,
  name_spell_original: row.name,
  authority_from_gbif: row.authority || null,
  authority_year: row.authorityYear ?? null,
  current_rank: row.statusNorm || null,
  current_parent: currentParentId || null,
  original_parent: originalParentId || null,
  valid_name_id: validNameId || null,
  last_update: new Date().toISOString(),
  remark: remarkTag,
});

const buildUpdatePayload = ({ dbRow, row, originalParentId, validNameId, remarkTag }) => {
  const updates = {};
  if (!dbRow.authority_year && row.authorityYear) updates.authority_year = row.authorityYear;
  if (!dbRow.authority_from_gbif && row.authority) updates.authority_from_gbif = row.authority;
  if (!dbRow.original_parent && originalParentId) updates.original_parent = originalParentId;
  if (!dbRow.valid_name_id && validNameId) updates.valid_name_id = validNameId;
  if (Object.keys(updates).length) {
    updates.last_update = new Date().toISOString();
    const nextRemark = appendRemark(dbRow.remark, remarkTag);
    if (nextRemark) updates.remark = nextRemark;
  }
  return updates;
};

const buildParentPreviewPayload = ({
  id,
  row,
  currentParentId,
  originalParentId,
  validNameId,
  remark,
}) =>
  omitUndefined({
    id: id || null,
    name_spell_valid: row?.name || null,
    name_spell_original: row?.name || null,
    authority_from_gbif: row?.authority || null,
    authority_year: row?.authorityYear ?? null,
    current_rank: row?.statusNorm || null,
    current_parent: currentParentId || null,
    original_parent: originalParentId || null,
    valid_name_id: validNameId || null,
    remark: remark || null,
  });

const buildTaxapadInsertPayload = ({ row, optionalColumns }) =>
  omitUndefined({
    id: row.mappedTaxonId || row.taxonId,
    current_parent: row.parentId || null,
    current_rank: row.currentRank || null,
    current_invalid_type: row.currentInvalidType || null,
    name_rank: optionalColumns?.name_rank ? row.nameRank || null : undefined,
    name_spell_valid: row.name,
    name_spell_original: row.nameSpellOriginal || null,
    authority_from_gbif: row.authorityFromGbif || null,
    authority_year: row.authorityYear ?? null,
    original_parent_remark: optionalColumns?.original_parent_remark
      ? row.originalParentRemark || null
      : undefined,
    extant_fossil: row.extantFossil || null,
    remark: row.notes || null,
    last_update: new Date().toISOString(),
  });

const buildTaxapadUpdatePayload = ({ dbRow, row, optionalColumns }) => {
  const updates = {};
  if (!dbRow.name_spell_valid && row.name) updates.name_spell_valid = row.name;
  if (!dbRow.name_spell_original && row.nameSpellOriginal) {
    updates.name_spell_original = row.nameSpellOriginal;
  }
  if (!dbRow.current_rank && row.currentRank) updates.current_rank = row.currentRank;
  if (!dbRow.current_invalid_type && row.currentInvalidType) {
    updates.current_invalid_type = row.currentInvalidType;
  }
  if (!dbRow.current_parent && row.parentId) updates.current_parent = row.parentId;
  if (optionalColumns?.name_rank && !dbRow.name_rank && row.nameRank) {
    updates.name_rank = row.nameRank;
  }
  if (
    optionalColumns?.original_parent_remark &&
    !dbRow.original_parent_remark &&
    row.originalParentRemark
  ) {
    updates.original_parent_remark = row.originalParentRemark;
  }
  if (!dbRow.authority_from_gbif && row.authorityFromGbif) {
    updates.authority_from_gbif = row.authorityFromGbif;
  }
  if (!dbRow.authority_year && row.authorityYear != null) {
    updates.authority_year = row.authorityYear;
  }
  if (!dbRow.extant_fossil && row.extantFossil) {
    updates.extant_fossil = row.extantFossil;
  }
  if (!dbRow.remark && row.notes) updates.remark = row.notes;
  if (Object.keys(updates).length) {
    updates.last_update = new Date().toISOString();
  }
  return omitUndefined(updates);
};

const resolveRowFromValueGetter = (paramsOrValue, maybeRow) => {
  if (maybeRow && typeof maybeRow === "object") return maybeRow;
  if (paramsOrValue && typeof paramsOrValue === "object") {
    if (paramsOrValue.row) return paramsOrValue.row;
    if ("rowIndex" in paramsOrValue || "action" in paramsOrValue || "previewPayload" in paramsOrValue) {
      return paramsOrValue;
    }
  }
  return null;
};

const getPreviewValue = (paramsOrValue, maybeRow, key) => {
  const row = resolveRowFromValueGetter(paramsOrValue, maybeRow);
  const payload = row?.previewPayload || row?.insertPayload || row?.updatePayload || {};
  const value = payload?.[key];
  return value == null ? "" : value;
};

export default function DialogScientificNameExcelImport({ open, onClose }) {
  const { showStatus } = useStatusDialog() || {};
  const fileInputRef = useRef(null);

  const [parentId, setParentId] = useState("");
  const [parentRecord, setParentRecord] = useState(null);
  const [parentLoading, setParentLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileFormat, setFileFormat] = useState(FORMAT_PARENT);
  const [parsedRows, setParsedRows] = useState([]);
  const [analysisRows, setAnalysisRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState(() => createSelectionModel());
  const [parsing, setParsing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [result, setResult] = useState(null);

  const resetState = useCallback(() => {
    setParentId("");
    setParentRecord(null);
    setParentLoading(false);
    setFile(null);
    setFileFormat(FORMAT_PARENT);
    setParsedRows([]);
    setAnalysisRows([]);
    setSelectionModel(createSelectionModel());
    setParsing(false);
    setAnalyzing(false);
    setImporting(false);
    setError("");
    setWarning("");
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  useEffect(() => {
    if (!parentId) {
      setParentRecord(null);
      setAnalysisRows([]);
      setSelectionModel(createSelectionModel());
      return;
    }
    let cancelled = false;
    setParentLoading(true);
    supabase
      .from("scientific_names")
      .select(
        "id,name_spell_valid,name_spell_original,current_rank,current_parent,original_parent,authority_year,authority_from_gbif,valid_name_id,remark"
      )
      .eq("id", parentId)
      .maybeSingle()
      .then(({ data, error: fetchError }) => {
        if (cancelled) return;
        if (fetchError) {
          setError(fetchError.message || "Failed to load the selected parent.");
          setParentRecord(null);
        } else {
          setParentRecord(data || null);
        }
      })
      .finally(() => {
        if (!cancelled) setParentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parentId]);

  const handleFileChange = useCallback(async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setParsedRows([]);
    setAnalysisRows([]);
    setSelectionModel(createSelectionModel());
    setError("");
    setWarning("");
    setResult(null);
    setParsing(true);
    try {
      const parsed = await parseExcelFile(selected);
      setParsedRows(parsed.rows || []);
      setFileFormat(parsed.format || FORMAT_PARENT);
      if (!parsed.rows?.length) setError("No usable rows found in the Excel file.");
    } catch (err) {
      setError(err.message || "Failed to parse the Excel file.");
    } finally {
      setParsing(false);
    }
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!parsedRows.length) {
      setError("Upload an Excel file first.");
      return;
    }
    if (fileFormat !== FORMAT_TAXAPAD && !parentRecord?.id) {
      setError("Select a root taxon first.");
      return;
    }
    setAnalyzing(true);
    setError("");
    setWarning("");
    setResult(null);

    try {
      if (fileFormat === FORMAT_TAXAPAD) {
        const plan = [];
        const validRows = [];
        const rowByTaxonId = new Map();
        const rowIdByTaxonId = new Map();
        const taxonIds = [];
        const rootLabel = parentRecord?.id
          ? parentRecord?.name_spell_valid || parentRecord?.name_spell_original || parentRecord?.id || ""
          : "";
        const rootNameKey = rootLabel ? normalizeKey(rootLabel) : "";
        const rootCandidates = [];
        let rootWarning = "";

        parsedRows.forEach((raw) => {
          const rowIndex = raw.rowIndex;
          const taxonId = normalizeText(raw.taxonId);
          const parentId = normalizeParentId(raw.parentId);
          const rank = normalizeText(raw.rank);
          const nameRank = normalizeText(raw.nameRank);
          const taxonomicStatus = normalizeText(raw.taxonomicStatus);
          const taxonomicRelation = normalizeText(raw.taxonomicRelation);
          const scientificName = formatScientificNameByRank(raw.scientificName, raw.rank);
          const authorship = normalizeText(raw.authorship);
          const year = normalizeText(raw.year);
          const authorInParens = parseBool(raw.authorInParens);
          const originalParentRemark = normalizeText(raw.originalParent);
          const notes = normalizeText(raw.notes);
          const indent = parseInteger(raw.indent);
          const lineNo = parseInteger(raw.lineNo);

          const rowId = `row-${rowIndex}`;
          const authorityFromGbif = buildAuthorityFromParts(authorship, year, authorInParens);
          const statusNorm = normalizeRank(rank);
          const isSynonym = [statusNorm, taxonomicStatus, taxonomicRelation].some(isSynonymLabel);
          const currentRank = isSynonym ? "invalid" : statusNorm || null;
          const currentInvalidType = isSynonym ? "synonym" : null;
          const nameSpellOriginal = scientificName ? `${scientificName} (temp data)` : null;
          const extantFossil = notes.toLowerCase().includes("fossil") ? "fossil" : null;

          const baseRow = {
            id: rowId,
            rowIndex,
            taxonId,
            parentId,
            name: scientificName,
            nameKey: normalizeKey(scientificName),
            status: rank,
            statusNorm: currentRank || statusNorm || "",
            currentRank,
            currentInvalidType,
            nameRank,
            taxonomicStatus,
            taxonomicRelation,
            authority: authorityFromGbif || authorship,
            authorityFromGbif,
            year,
            authorityYear: parseYear(year),
            authorInParens,
            originalParentRemark,
            notes,
            extantFossil,
            nameSpellOriginal,
            indent,
            lineNo,
            depth: indent ?? null,
            currentParentLabel: parentId || "",
            originalParentLabel: originalParentRemark || "",
            isInvalid: currentRank === "invalid",
          };

          if (!taxonId || !scientificName) {
            plan.push({
              ...baseRow,
              action: "skip",
              note: "Missing TAXON_ID or SCIENTIFIC_NAME.",
            });
            return;
          }

          if (rowIdByTaxonId.has(taxonId)) {
            plan.push({
              ...baseRow,
              action: "skip",
              note: "Duplicate TAXON_ID in Excel.",
            });
            return;
          }

          rowByTaxonId.set(taxonId, baseRow);
          rowIdByTaxonId.set(taxonId, rowId);
          validRows.push(baseRow);
          if (rootNameKey && baseRow.nameKey === rootNameKey) {
            rootCandidates.push(baseRow);
          }
          taxonIds.push(taxonId);
        });

        const rootMapping = new Map();
        let primaryRootTaxonId = null;
        if (rootNameKey) {
          if (!rootCandidates.length) {
            rootWarning = "Root scientific name not found in the file; root remap skipped.";
          } else {
            const preferred =
              rootCandidates.find(
                (row) =>
                  normalizeRank(row.currentRank) === normalizeRank(parentRecord?.current_rank)
              ) || rootCandidates[0];
            primaryRootTaxonId = preferred.taxonId;
            rootCandidates.forEach((row) => {
              rootMapping.set(row.taxonId, parentRecord.id);
              row.mappedTaxonId = parentRecord.id;
              row.isRootMatch = row.taxonId === primaryRootTaxonId;
            });
          }
        }

        if (rootMapping.size) {
          validRows.forEach((row) => {
            if (rootMapping.has(row.parentId)) {
              row.parentId = rootMapping.get(row.parentId);
              row.currentParentLabel = rootLabel || row.currentParentLabel;
            }
          });
        }

        if (!validRows.length) {
          plan.forEach((row) => {
            row.previewPayload = buildTaxapadInsertPayload({ row, optionalColumns: {} });
          });
          setAnalysisRows(plan);
          setSelectionModel(createSelectionModel());
          if (!plan.length) setError("No usable rows found in the Excel file.");
          return;
        }

        const { available: optionalColumns, warning: columnWarning } = await detectOptionalColumns([
          "name_rank",
          "original_parent_remark",
        ]);
        const combinedWarning = [rootWarning, columnWarning].filter(Boolean).join(" ");
        if (combinedWarning) setWarning(combinedWarning);

        const depthByTaxonId = new Map();
        const computeDepth = (row, stack = new Set()) => {
          if (!row) return 0;
          if (row.indent != null) return row.indent;
          if (depthByTaxonId.has(row.taxonId)) return depthByTaxonId.get(row.taxonId);
          if (stack.has(row.taxonId)) return 0;
          stack.add(row.taxonId);
          if (!row.parentId) {
            depthByTaxonId.set(row.taxonId, 0);
            return 0;
          }
          const parentRow = rowByTaxonId.get(row.parentId);
          const parentDepth = parentRow ? computeDepth(parentRow, stack) : 0;
          const depth = parentDepth + 1;
          depthByTaxonId.set(row.taxonId, depth);
          return depth;
        };

        validRows.forEach((row) => {
          if (row.depth == null) row.depth = computeDepth(row);
        });

        const selectFields = [
          "id",
          "name_spell_valid",
          "name_spell_original",
          "current_rank",
          "current_parent",
          "current_invalid_type",
          "authority_from_gbif",
          "authority_year",
          "extant_fossil",
          "remark",
          ...(optionalColumns?.name_rank ? ["name_rank"] : []),
          ...(optionalColumns?.original_parent_remark ? ["original_parent_remark"] : []),
        ].join(",");

        const existingRows = await selectInBatched({
          table: "scientific_names",
          column: "id",
          values: taxonIds,
          select: selectFields,
        });
        const existingMap = new Map();
        existingRows.forEach((row) => {
          existingMap.set(String(row.id), row);
        });

        const parentIds = Array.from(
          new Set(validRows.map((row) => row.parentId).filter(Boolean))
        );
        const parentIdsToCheck = parentIds.filter(
          (id) => !existingMap.has(id) && !rowByTaxonId.has(id)
        );
        const parentRows = parentIdsToCheck.length
          ? await selectInBatched({
              table: "scientific_names",
              column: "id",
              values: parentIdsToCheck,
              select: "id,name_spell_valid",
            })
          : [];
        const parentDbMap = new Map();
        parentRows.forEach((row) => {
          parentDbMap.set(String(row.id), row);
        });

        const planByTaxonId = new Map();
        validRows.forEach((row) => {
          const isRootMapped = rootMapping.has(row.taxonId);
          const isPrimaryRoot = isRootMapped && row.taxonId === primaryRootTaxonId;
          if (isRootMapped && !isPrimaryRoot) {
            const planRow = {
              ...row,
              action: "skip",
              note: "Duplicate root row mapped to selected root.",
              dbId: parentRecord?.id || null,
              updatePayload: {},
            };
            plan.push(planRow);
            planByTaxonId.set(row.taxonId, planRow);
            return;
          }

          const dbRow = isPrimaryRoot && parentRecord?.id ? parentRecord : existingMap.get(row.taxonId);
          const noteParts = [];
          if (dbRow) {
            if (dbRow.name_spell_valid && normalizeKey(dbRow.name_spell_valid) !== row.nameKey) {
              noteParts.push("Name differs from DB.");
            }
            if (dbRow.current_parent && row.parentId && String(dbRow.current_parent) !== row.parentId) {
              noteParts.push("Parent differs from DB.");
            }
            if (dbRow.current_rank && row.currentRank && normalizeRank(dbRow.current_rank) !== row.currentRank) {
              noteParts.push("Rank differs from DB.");
            }
            const updatePayload = buildTaxapadUpdatePayload({ dbRow, row, optionalColumns });
            const action = Object.keys(updatePayload).length ? "merge" : "skip";
            const note = [
              isPrimaryRoot ? "Root mapped to selected record." : null,
              action === "merge" ? "Existing record will be updated." : "Duplicate already exists.",
              ...noteParts,
            ]
              .filter(Boolean)
              .join(" ");
            const planRow = {
              ...row,
              action,
              note,
              dbId: dbRow.id,
              updatePayload,
            };
            plan.push(planRow);
            planByTaxonId.set(row.taxonId, planRow);
          } else {
            const planRow = {
              ...row,
              action: "insert",
              note: noteParts.join(" "),
              newId: row.mappedTaxonId || row.taxonId,
              insertPayload: buildTaxapadInsertPayload({ row, optionalColumns }),
            };
            plan.push(planRow);
            planByTaxonId.set(row.taxonId, planRow);
          }
        });

        plan.forEach((row) => {
          const parentId = row.parentId;
          if (!parentId) return;
          const parentPlan = planByTaxonId.get(parentId);
          if (parentPlan) {
            row.currentParentLabel = parentPlan.name || parentId;
            if (parentPlan.action === "insert") {
              row.parentIsNew = true;
              row.parentPlanRowId = parentPlan.id;
            }
            if (
              row.action === "merge" &&
              row.updatePayload?.current_parent &&
              parentPlan.action === "insert"
            ) {
              row.parentIsNew = true;
              row.parentPlanRowId = parentPlan.id;
            }
            return;
          }
          const parentDb = existingMap.get(parentId) || parentDbMap.get(parentId);
          if (parentDb) {
            row.currentParentLabel = parentDb.name_spell_valid || parentId;
            return;
          }
          const note = row.note ? `${row.note} ` : "";
          row.note = `${note}Parent ID not found in file or DB.`;
        });

        plan.forEach((row) => {
          row.previewPayload = buildTaxapadInsertPayload({ row, optionalColumns });
        });

        const sortKey = (row) => row.lineNo ?? row.rowIndex ?? 0;
        const sortedPlan = plan.sort((a, b) => sortKey(a) - sortKey(b));
        setAnalysisRows(sortedPlan);
        setSelectionModel(
          createSelectionModel(
            sortedPlan
              .filter((r) => r.action === "insert" || r.action === "merge")
              .map((r) => String(r.id))
          )
        );
        return;
      }

      const remarkTag = buildRemarkTag(file?.name || "");
      const remarkForInsert = appendRemark("", remarkTag);
      const rootLabel =
        parentRecord?.name_spell_valid || parentRecord?.name_spell_original || parentRecord?.id || "";
      const rootNameKey = normalizeKey(rootLabel);
      if (!rootNameKey) {
        setError("Selected root has no name to match.");
        return;
      }

      const plan = [];
      const handledRowIds = new Set();
      const seenKeys = new Set();
      const excelRows = [];
      const excelChildrenMap = new Map();
      const rootMatchRows = [];

      const addChild = (key, row) => {
        if (!key) return;
        if (!excelChildrenMap.has(key)) excelChildrenMap.set(key, []);
        excelChildrenMap.get(key).push(row);
      };

      parsedRows.forEach((raw) => {
        const statusNorm = normalizeRank(raw.status);
        const name = formatScientificNameByRank(raw.name, raw.status);
        const nameKey = normalizeKey(name);
        const currentParentKey = normalizeKey(raw.currentParentName);
        const originalParentKey = normalizeKey(raw.originalParentName);
        const row = {
          id: `row-${raw.rowIndex}`,
          rowIndex: raw.rowIndex,
          status: raw.status,
          statusNorm,
          name,
          nameKey,
          currentParentName: raw.currentParentName,
          currentParentKey,
          originalParentName: raw.originalParentName,
          originalParentKey,
          authority: raw.authority,
          year: raw.year,
          authorityYear: parseYear(raw.year),
          isInvalid: isInvalidStatus(statusNorm),
        };
        excelRows.push(row);

        if (!statusNorm || !nameKey) {
          plan.push({
            ...row,
            action: "skip",
            note: "Missing status or scientific name.",
            previewPayload: buildParentPreviewPayload({
              id: null,
              row,
              currentParentId: null,
              originalParentId: null,
              validNameId: null,
              remark: null,
            }),
          });
          handledRowIds.add(row.id);
          return;
        }

        if (nameKey === rootNameKey) {
          rootMatchRows.push(row);
          return;
        }

        if (!currentParentKey) {
          plan.push({
            ...row,
            action: "skip",
            note: "Missing current parent.",
            previewPayload: buildParentPreviewPayload({
              id: null,
              row,
              currentParentId: null,
              originalParentId: null,
              validNameId: null,
              remark: null,
            }),
          });
          handledRowIds.add(row.id);
          return;
        }

        addChild(currentParentKey, row);
      });

      const parentByNameKey = new Map();
      parentByNameKey.set(rootNameKey, {
        id: parentRecord.id,
        label: rootLabel,
        exists: true,
      });

      const resolveOriginalParentId = (row) => {
        if (!row?.originalParentKey) return null;
        if (row.originalParentKey === rootNameKey) return parentRecord.id;
        const hit = parentByNameKey.get(row.originalParentKey);
        return hit ? hit.id : null;
      };

      if (!rootMatchRows.length) {
        setWarning("Root scientific name not found in the file; root merge skipped.");
      } else {
        let rootMerged = false;
        rootMatchRows.forEach((row) => {
          if (rootMerged) {
            plan.push({
              ...row,
              action: "skip",
              note: "Duplicate root row in file.",
              currentParentLabel: rootLabel,
              originalParentLabel: row.originalParentName || "",
              previewPayload: buildParentPreviewPayload({
                id: parentRecord.id,
                row,
                currentParentId: parentRecord.current_parent || null,
                originalParentId: resolveOriginalParentId(row),
                validNameId: null,
                remark: null,
              }),
            });
            handledRowIds.add(row.id);
            return;
          }

          const noteParts = [];
          if (
            row.statusNorm &&
            parentRecord.current_rank &&
            normalizeRank(parentRecord.current_rank) !== row.statusNorm
          ) {
            noteParts.push(`Rank mismatch: excel ${row.statusNorm} vs db ${parentRecord.current_rank}`);
          }
          const originalParentId = resolveOriginalParentId(row);
          if (row.originalParentName && !originalParentId) {
            noteParts.push(`Original parent not found: ${row.originalParentName}`);
          }

          const updatePayload = buildUpdatePayload({
            dbRow: parentRecord,
            row,
            originalParentId,
            validNameId: null,
            remarkTag,
          });
          const action = Object.keys(updatePayload).length ? "merge" : "skip";
          const note = [
            action === "merge" ? "Root record will be updated." : "Root matches selected record.",
            ...noteParts,
          ]
            .filter(Boolean)
            .join(" ");
          plan.push({
            ...row,
            action,
            note,
            dbId: parentRecord.id,
            updatePayload,
            currentParentLabel: rootLabel,
            originalParentLabel: row.originalParentName || "",
            depth: 0,
            previewPayload: buildParentPreviewPayload({
              id: parentRecord.id,
              row,
              currentParentId: parentRecord.current_parent || null,
              originalParentId,
              validNameId: null,
              remark: updatePayload.remark || null,
            }),
          });
          handledRowIds.add(row.id);
          rootMerged = true;
        });
      }

      const queue = [
        {
          id: parentRecord.id,
          nameKey: rootNameKey,
          label: rootLabel,
          exists: true,
          depth: 0,
          planRowId: null,
        },
      ];
      const processedParents = new Set();

      const enqueueChild = (child) => {
        if (!child?.nameKey || !child?.id) return;
        if (processedParents.has(child.id)) return;
        if (!excelChildrenMap.has(child.nameKey)) return;
        queue.push(child);
      };

      const pickDbMatch = (candidates, statusNorm) => {
        if (!candidates?.length) return null;
        const exact = candidates.find((c) => normalizeRank(c.current_rank) === statusNorm);
        return exact || candidates[0] || null;
      };

      const processParent = (parentCtx, dbChildren = []) => {
        const excelChildren = excelChildrenMap.get(parentCtx.nameKey) || [];
        if (!excelChildren.length) return;

        const dbChildrenByName = new Map();
        dbChildren.forEach((child) => {
          const key = normalizeKey(child.name_spell_valid);
          if (!key) return;
          if (!dbChildrenByName.has(key)) dbChildrenByName.set(key, []);
          dbChildrenByName.get(key).push(child);
        });

        excelChildren.forEach((row) => {
          if (handledRowIds.has(row.id)) return;

          const dedupeKey = `${parentCtx.nameKey}__${row.nameKey}__${row.statusNorm}`;
          const originalParentId = resolveOriginalParentId(row);
          if (seenKeys.has(dedupeKey)) {
            plan.push({
              ...row,
              action: "skip",
              note: "Duplicate row under the same parent.",
              currentParentLabel: parentCtx.label,
              originalParentLabel: row.originalParentName || "",
              isInvalid: row.isInvalid,
              depth: parentCtx.depth + 1,
              previewPayload: buildParentPreviewPayload({
                id: null,
                row,
                currentParentId: parentCtx.id,
                originalParentId,
                validNameId: row.isInvalid ? parentCtx.id : null,
                remark: null,
              }),
            });
            handledRowIds.add(row.id);
            return;
          }
          seenKeys.add(dedupeKey);

          const noteParts = [];
          if (row.currentParentKey && row.currentParentKey !== parentCtx.nameKey) {
            noteParts.push(`Parent mismatch: ${row.currentParentName}`);
          }
          if (row.originalParentName && !originalParentId) {
            noteParts.push(`Original parent not found: ${row.originalParentName}`);
          }

          const dbCandidates = dbChildrenByName.get(row.nameKey) || [];
          const dbMatch = parentCtx.exists ? pickDbMatch(dbCandidates, row.statusNorm) : null;

          if (dbCandidates.length > 1) {
            noteParts.push("Multiple DB matches found; using first.");
          }

          if (dbMatch && row.statusNorm && normalizeRank(dbMatch.current_rank) !== row.statusNorm) {
            noteParts.push(`Rank mismatch: excel ${row.statusNorm} vs db ${dbMatch.current_rank}`);
          }

          const basePayload = {
            ...row,
            currentParentLabel: parentCtx.label,
            originalParentLabel: row.originalParentName || "",
            isInvalid: row.isInvalid,
            depth: parentCtx.depth + 1,
            parentIsNew: !parentCtx.exists,
            parentPlanRowId: parentCtx.planRowId || null,
          };

          if (dbMatch) {
            const updatePayload = buildUpdatePayload({
              dbRow: dbMatch,
              row,
              originalParentId,
              validNameId: row.isInvalid ? parentCtx.id : null,
              remarkTag,
            });
            const action = Object.keys(updatePayload).length ? "merge" : "skip";
            const note = [
              action === "merge" ? "Existing record will be updated." : "Duplicate already exists.",
              ...noteParts,
            ]
              .filter(Boolean)
              .join(" ");
            plan.push({
              ...basePayload,
              action,
              note,
              dbId: dbMatch.id,
              updatePayload,
              previewPayload: buildParentPreviewPayload({
                id: dbMatch.id,
                row,
                currentParentId: parentCtx.id,
                originalParentId,
                validNameId: row.isInvalid ? parentCtx.id : null,
                remark: updatePayload.remark || null,
              }),
            });
            handledRowIds.add(row.id);

            if (!parentByNameKey.has(row.nameKey)) {
              parentByNameKey.set(row.nameKey, {
                id: dbMatch.id,
                label: dbMatch.name_spell_valid || row.name,
                exists: true,
              });
            }

            enqueueChild({
              id: dbMatch.id,
              nameKey: row.nameKey,
              label: dbMatch.name_spell_valid || row.name,
              exists: true,
              depth: parentCtx.depth + 1,
              planRowId: null,
            });
          } else {
            const newId = generateId();
            plan.push({
              ...basePayload,
              action: "insert",
              note: noteParts.join(" "),
              newId,
              insertPayload: buildInsertPayload({
                row: { ...row, newId },
                currentParentId: parentCtx.id,
                originalParentId,
                validNameId: row.isInvalid ? parentCtx.id : null,
                remarkTag: remarkForInsert,
              }),
              previewPayload: buildParentPreviewPayload({
                id: newId,
                row,
                currentParentId: parentCtx.id,
                originalParentId,
                validNameId: row.isInvalid ? parentCtx.id : null,
                remark: remarkForInsert || null,
              }),
            });
            handledRowIds.add(row.id);

            if (!parentByNameKey.has(row.nameKey)) {
              parentByNameKey.set(row.nameKey, {
                id: newId,
                label: row.name,
                exists: false,
              });
            }

            enqueueChild({
              id: newId,
              nameKey: row.nameKey,
              label: row.name,
              exists: false,
              depth: parentCtx.depth + 1,
              planRowId: row.id,
            });
          }
        });
      };

      let cursor = 0;
      while (cursor < queue.length) {
        const batchContexts = [];
        const batchIds = [];
        const nextIndex = queue.length;
        for (; cursor < nextIndex && batchIds.length < 200; cursor += 1) {
          const ctx = queue[cursor];
          if (!ctx?.id || processedParents.has(ctx.id)) continue;
          processedParents.add(ctx.id);
          if (!excelChildrenMap.has(ctx.nameKey)) continue;
          if (ctx.exists) {
            batchContexts.push(ctx);
            batchIds.push(ctx.id);
          } else {
            processParent(ctx, []);
          }
        }

        if (batchIds.length) {
          const dbChildren = await selectInBatched({
            table: "scientific_names",
            column: "current_parent",
            values: batchIds,
            select:
              "id,name_spell_valid,current_rank,current_parent,original_parent,authority_year,authority_from_gbif,valid_name_id,remark",
          });
          const grouped = new Map();
          dbChildren.forEach((row) => {
            const parentId = row.current_parent;
            if (!parentId) return;
            if (!grouped.has(parentId)) grouped.set(parentId, []);
            grouped.get(parentId).push(row);
          });
          batchContexts.forEach((ctx) => processParent(ctx, grouped.get(ctx.id) || []));
        }
      }

      excelRows.forEach((row) => {
        if (handledRowIds.has(row.id)) return;
        const originalParentId = resolveOriginalParentId(row);
        plan.push({
          ...row,
          action: "skip",
          note: "Parent not matched in traversal.",
          currentParentLabel: row.currentParentName || "",
          originalParentLabel: row.originalParentName || "",
          isInvalid: row.isInvalid,
          previewPayload: buildParentPreviewPayload({
            id: null,
            row,
            currentParentId: null,
            originalParentId,
            validNameId: null,
            remark: null,
          }),
        });
        handledRowIds.add(row.id);
      });

      const sortedPlan = plan.sort((a, b) => (a.rowIndex || 0) - (b.rowIndex || 0));
      setAnalysisRows(sortedPlan);
      setSelectionModel(
        createSelectionModel(
          sortedPlan
            .filter((r) => r.action === "insert" || r.action === "merge")
            .map((r) => String(r.id))
        )
      );
    } catch (err) {
      console.error("[Excel import] analyze failed", err);
      setError(err.message || "Failed to analyze Excel rows.");
    } finally {
      setAnalyzing(false);
    }
  }, [file, fileFormat, parentRecord, parsedRows]);

  const handleImport = useCallback(async () => {
    const selectedSet = new Set(selectionModelIds(selectionModel));
    const selectedRows = analysisRows.filter(
      (row) =>
        selectedSet.has(String(row.id)) && (row.action === "insert" || row.action === "merge")
    );
    if (!selectedRows.length) {
      setError("No rows selected for import.");
      return;
    }

    const parentDependencyErrors = selectedRows.filter(
      (row) =>
        row.parentIsNew &&
        row.parentPlanRowId &&
        !selectedSet.has(String(row.parentPlanRowId))
    );
    if (parentDependencyErrors.length) {
      setError("Some selected rows depend on new parent taxa that are not selected for insert.");
      return;
    }

    setImporting(true);
    setError("");
    try {
      const insertRows = selectedRows
        .filter((row) => row.action === "insert" && row.insertPayload)
        .sort((a, b) => {
          const depthDiff = (a.depth || 0) - (b.depth || 0);
          if (depthDiff !== 0) return depthDiff;
          return (a.rowIndex || 0) - (b.rowIndex || 0);
        });
      const mergeRows = selectedRows.filter(
        (row) => row.action === "merge" && row.updatePayload && Object.keys(row.updatePayload).length
      );

      const runInsertBatches = async (rows) => {
        let count = 0;
        for (const batch of chunk(rows, 200)) {
          const payload = batch.map((row) => row.insertPayload).filter(Boolean);
          if (!payload.length) continue;
          const { error: insertError } = await supabase.from("scientific_names").insert(payload);
          if (insertError) throw insertError;
          count += payload.length;
        }
        return count;
      };

      const insertedCount = await runInsertBatches(insertRows);

      let updatedCount = 0;
      for (const row of mergeRows) {
        const { error: updateError } = await supabase
          .from("scientific_names")
          .update(row.updatePayload)
          .eq("id", row.dbId);
        if (updateError) throw updateError;
        updatedCount += 1;
      }

      const summary = { inserted: insertedCount, updated: updatedCount };
      setResult(summary);
      showStatus?.({
        severity: "success",
        title: "Excel import completed",
        description: `Inserted ${insertedCount}, updated ${updatedCount}.`,
      });
    } catch (err) {
      console.error("[Excel import] import failed", err);
      setError(err.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  }, [analysisRows, selectionModel, showStatus]);

  const analysisStats = useMemo(() => {
    const counts = { insert: 0, merge: 0, skip: 0 };
    analysisRows.forEach((row) => {
      if (row.action === "insert") counts.insert += 1;
      if (row.action === "merge") counts.merge += 1;
      if (row.action === "skip") counts.skip += 1;
    });
    return counts;
  }, [analysisRows]);

  const columns = useMemo(
    () => [
      { field: "rowIndex", headerName: "row", width: 80 },
      {
        field: "action",
        headerName: "action",
        width: 120,
        renderCell: (params) => {
          const action = params?.row?.action || "";
          const color = action === "insert" ? "success" : action === "merge" ? "info" : "default";
          const label = action ? action.toUpperCase() : "-";
          return <Chip label={label} color={color} size="small" />;
        },
      },
      {
        field: "id",
        headerName: "id",
        width: 140,
        valueGetter: (value, row) =>
          getPreviewValue(value, row, "id") || row?.dbId || row?.newId || "",
      },
      {
        field: "current_parent",
        headerName: "current_parent",
        width: 160,
        valueGetter: (value, row) => getPreviewValue(value, row, "current_parent"),
      },
      {
        field: "current_rank",
        headerName: "current_rank",
        width: 140,
        valueGetter: (value, row) => getPreviewValue(value, row, "current_rank"),
      },
      {
        field: "current_invalid_type",
        headerName: "current_invalid_type",
        width: 160,
        valueGetter: (value, row) => getPreviewValue(value, row, "current_invalid_type"),
      },
      {
        field: "name_rank",
        headerName: "name_rank",
        width: 140,
        valueGetter: (value, row) => getPreviewValue(value, row, "name_rank"),
      },
      {
        field: "name_spell_valid",
        headerName: "name_spell_valid",
        minWidth: 200,
        flex: 1,
        valueGetter: (value, row) => getPreviewValue(value, row, "name_spell_valid"),
      },
      {
        field: "name_spell_original",
        headerName: "name_spell_original",
        minWidth: 200,
        flex: 1,
        valueGetter: (value, row) => getPreviewValue(value, row, "name_spell_original"),
      },
      {
        field: "authority_from_gbif",
        headerName: "authority_from_gbif",
        width: 200,
        valueGetter: (value, row) => getPreviewValue(value, row, "authority_from_gbif"),
      },
      {
        field: "authority_year",
        headerName: "authority_year",
        width: 130,
        valueGetter: (value, row) => getPreviewValue(value, row, "authority_year"),
      },
      {
        field: "original_parent",
        headerName: "original_parent",
        width: 160,
        valueGetter: (value, row) => getPreviewValue(value, row, "original_parent"),
      },
      {
        field: "original_parent_remark",
        headerName: "original_parent_remark",
        width: 200,
        valueGetter: (value, row) => getPreviewValue(value, row, "original_parent_remark"),
      },
      {
        field: "extant_fossil",
        headerName: "extant_fossil",
        width: 120,
        valueGetter: (value, row) => getPreviewValue(value, row, "extant_fossil"),
      },
      {
        field: "remark",
        headerName: "remark",
        minWidth: 220,
        flex: 1,
        valueGetter: (value, row) => getPreviewValue(value, row, "remark"),
      },
      {
        field: "valid_name_id",
        headerName: "valid_name_id",
        width: 160,
        valueGetter: (value, row) => getPreviewValue(value, row, "valid_name_id"),
      },
      { field: "note", headerName: "note", minWidth: 220, flex: 1 },
    ],
    [fileFormat]
  );

  const canAnalyze =
    parsedRows.length > 0 &&
    !analyzing &&
    (fileFormat === FORMAT_TAXAPAD || Boolean(parentRecord?.id));
  const canImport = analysisRows.length > 0 && selectionModelIds(selectionModel).length > 0 && !importing;
  const didUpdate = (result?.inserted || 0) + (result?.updated || 0) > 0;

  return (
    <Dialog open={open} onClose={() => onClose?.(didUpdate)} maxWidth="lg" fullWidth>
      <DialogTitle>Excel Import: Scientific Names</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">Step 1: Select root taxon</Typography>
              <ScientificNameSelector
                label="Root taxon"
                valueId={parentId}
                value={parentId}
                onChange={(id) => setParentId(id || "")}
                showInputLabel
                inlineAdd={false}
                isRequired={fileFormat !== FORMAT_TAXAPAD}
              />
              {fileFormat === FORMAT_TAXAPAD && (
                <Alert severity="info">
                  Taxapad format detected. Root selection is optional; matching names will be merged and child
                  parents remapped.
                </Alert>
              )}
              {parentLoading && <CircularProgress size={20} />}
              {parentRecord && (
                <Alert severity="info">
                  Selected: {parentRecord.name_spell_valid || parentRecord.name_spell_original || parentRecord.id}{" "}
                  ({parentRecord.current_rank || "rank N/A"})
                </Alert>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">Step 2: Upload Excel file</Typography>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                Choose file
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </Button>
              {file && <Typography variant="body2">File: {file.name}</Typography>}
              {parsing && <CircularProgress size={20} />}
              {parsedRows.length > 0 && (
                <Typography variant="caption">{parsedRows.length} rows parsed.</Typography>
              )}
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={handleAnalyze} disabled={!canAnalyze}>
              {analyzing ? <CircularProgress size={20} /> : "Analyze"}
            </Button>
            {parsing && <Typography variant="caption">Parsing...</Typography>}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {warning && <Alert severity="warning">{warning}</Alert>}

          {analysisRows.length > 0 && (
            <>
              <Divider />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" color="success" label={`Insert: ${analysisStats.insert}`} />
                <Chip size="small" color="info" label={`Merge: ${analysisStats.merge}`} />
                <Chip size="small" label={`Skip: ${analysisStats.skip}`} />
                <Chip size="small" label={`Selected: ${selectionModelIds(selectionModel).length}`} />
              </Stack>
              <Box sx={{ height: 420, width: "100%" }}>
                <DataGrid
                  density="compact"
                  rows={analysisRows}
                  columns={columns}
                  checkboxSelection
                  disableRowSelectionOnClick
                  loading={analyzing || importing}
                  onRowSelectionModelChange={(model) => setSelectionModel(normalizeSelectionModel(model))}
                  rowSelectionModel={selectionModel}
                  isRowSelectable={(params) => params.row.action !== "skip"}
                  initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                  pageSizeOptions={[25, 50, 100]}
                />
              </Box>
            </>
          )}

          {result && (
            <Alert severity="success">
              Imported {result.inserted} rows, updated {result.updated} rows.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.(didUpdate)} disabled={parsing || analyzing || importing}>
          Close
        </Button>
        <Button onClick={resetState} disabled={parsing || analyzing || importing}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleImport} disabled={!canImport}>
          {importing ? <CircularProgress size={20} /> : "Import selected"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
